import { useCallback, useState } from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { useDispatch, useSelector } from 'react-redux';

import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';

export interface LinkedResource {
  uuid: string;
  name?: string | null;
  account?: string | null;
  resourceType?: string | null;
}

/**
 * Opens another resource in the drawer on its summary, such as a principal or
 * a role a principal assumes. Its details are looked up first, since the
 * drawer needs its type, account and provider.
 */
export const useOpenLinkedResource = () => {
  const dispatch = useDispatch();
  const { resourceAccountId, linkedResourceDetails } = useSelector(
    selectActiveResource,
  );
  const { updateActiveResource } = useActiveResourceActions();
  const [loadingUuid, setLoadingUuid] = useState<string | null>(null);

  const openResource = useCallback(
    (resource: LinkedResource) => {
      setLoadingUuid(resource.uuid);
      dispatch(
        actions.getLinkedResourceDetails({
          q: {
            insightV2Request: {
              resource_uuids: [resource.uuid],
              // With an account the lookup reads that account's snapshot; with
              // none it asks for the whole org's, which can come back empty.
              account_id: resource.account ? [resource.account] : undefined,
              resource_type:
                (resource.resourceType as NativeResources) ?? undefined,
            },
          },
          onSuccess: res => {
            const item = res?.data?.items?.[0];
            if (!item) return;
            const resourceType = (item?.native_name ??
              resource.resourceType) as NativeResources;
            updateActiveResource({
              resourceType,
              uuid: item?.uuid ?? resource.uuid,
              resourceId: item?.resource_id ?? resource.name ?? undefined,
              accountId: item?.account ?? resource.account ?? resourceAccountId,
              cloudProvider: item?.provider,
              tabIndex: 0,
            });
          },
        }),
      );
    },
    [dispatch, resourceAccountId, updateActiveResource],
  );

  const isOpening = useCallback(
    (uuid?: string | null) =>
      !!uuid && loadingUuid === uuid && linkedResourceDetails.isLoading,
    [linkedResourceDetails.isLoading, loadingUuid],
  );

  return { openResource, isOpening };
};
